import { type Factiii } from '@shared/all/domains/factiiis';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import storage from '../helpers/storage';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import useUser from './useUser';

// One key holds the whole pool. Readers look factiiis up by id/slug across
// everything fetched so far, so splitting per-procedure would break those
// lookups — every fetcher merges into this key instead. Nothing fetches it
// directly, which is why its observers pass skipToken.
export const FACTIIIS_KEY = ['factiiis'];

const EMPTY_FACTIIIS: Factiii[] = [];

// The response is per-user (the factiii space's factiiis plus your pins, with
// autoTagging derived from your permissions), so the stored copy is keyed by
// user and dropped on logout — see clearPersistedFactiiis.
const persistKey = (userId: number) => `factiiis:${userId}`;

const readPersisted = async (userId: number): Promise<Factiii[] | null> => {
  if (!userId) return null;
  try {
    const raw = await storage.getItem(persistKey(userId));
    return raw ? (JSON.parse(raw) as Factiii[]) : null;
  } catch {
    return null;
  }
};

const writePersisted = async (userId: number, factiiis: Factiii[]) => {
  if (!userId) return;
  try {
    await storage.setItem(persistKey(userId), JSON.stringify(factiiis));
  } catch {
    // A full or unavailable store only costs the next launch its head start.
  }
};

export const clearPersistedFactiiis = async (userId: number) => {
  if (!userId) return;
  try {
    await storage.removeItem(persistKey(userId));
  } catch {
    // Nothing to recover from — the key is per-user and unreadable anyway.
  }
};

// Deduped here rather than through fetchQuery so the boot fetch never registers
// as an active query: useIsFetching() gates the splash screen, and holding it
// for the factiii list would delay first paint for visitors who never see one.
let bootFetch: Promise<Factiii[] | null> | null = null;

const fetchBootSet = (client: WrappedTRPCClient) => {
  bootFetch ??= client.factiiis.initialItems
    .query()
    .then(([error, data]) => (error || !data ? null : data.factiiiList))
    .finally(() => {
      bootFetch = null;
    });
  return bootFetch;
};

const mergeById = (existing: Factiii[], incoming: Factiii[]) => {
  const byId = new Map(existing.map((factiii) => [factiii.id, factiii]));
  for (const factiii of incoming) byId.set(factiii.id, factiii);
  return [...byId.values()];
};

// Subscribes to the pool without fetching it. Exported for components that
// only look factiiis up and have no business holding a tRPC client (Avatar).
export const useFactiiisCache = (): Factiii[] => {
  const { data } = useQuery<Factiii[]>({
    queryKey: FACTIIIS_KEY,
    queryFn: skipToken
  });
  return data ?? EMPTY_FACTIIIS;
};

const useFactiiis = (client: WrappedTRPCClient) => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();
  const userId = currentUser.id;

  const factiiis = useFactiiisCache();

  const mergeFactiiis = (incoming: Factiii[]) =>
    queryClient.setQueryData(FACTIIIS_KEY, (current: Factiii[] | undefined) =>
      mergeById(current ?? EMPTY_FACTIIIS, incoming)
    );

  // Paints last launch's list while the network answers, then merges the fresh
  // one over it. Merging rather than replacing keeps whatever the session has
  // already looked up; only the boot set is stored, which is what the next
  // launch needs and bounds the stored blob to one response.
  const initializeFactiiis = async () => {
    if (!queryClient.getQueryData(FACTIIIS_KEY)) {
      const stored = await readPersisted(userId);
      if (stored?.length) queryClient.setQueryData(FACTIIIS_KEY, stored);
    }
    const bootSet = await fetchBootSet(client);
    if (!bootSet) return;
    mergeFactiiis(bootSet);
    void writePersisted(userId, bootSet);
  };

  // Replaces a loop that polled every 100ms and let concurrent callers each
  // fire their own request — they now await the same deduped boot fetch.
  const ensureFactiiis = async () => {
    const pool = queryClient.getQueryData<Factiii[]>(FACTIIIS_KEY);
    if (pool?.length) return pool;
    await initializeFactiiis();
    return queryClient.getQueryData<Factiii[]>(FACTIIIS_KEY) ?? EMPTY_FACTIIIS;
  };

  const querySlug = async (factiiiSlug: string) => {
    const [error, result] = await client.factiiis.factiiiItems.query({
      factiiiSlug
    });
    if (error || !result) return undefined;
    mergeFactiiis(result.factiiiList);
    return result.factiiiList[0];
  };

  const queryIds = async (factiiiIds: number[]) => {
    const [error, result] = await client.factiiis.factiiiItems.query({
      factiiiIds
    });
    if (error || !result) return undefined;
    mergeFactiiis(result.factiiiList);
    return result.factiiiList[0];
  };

  const getFactiiiSlug = (factiiiSlug: string) => {
    const factiii = factiiis.find(
      (factiii: Factiii) => factiii.slug === factiiiSlug
    );
    return factiii ?? querySlug(factiiiSlug);
  };

  const getFactiiiIds = async (factiiiIds: number[]): Promise<Factiii[]> => {
    const pool = await ensureFactiiis();
    const missingIds = factiiiIds.filter(
      (id) => !pool.some((factiii) => factiii.id === id)
    );

    let fetched: Factiii[] = [];
    if (missingIds.length > 0) {
      const [error, response] = await client.factiiis.factiiiItems.query({
        factiiiIds: missingIds
      });
      if (error || !response) return [];
      mergeFactiiis(response.factiiiList);
      fetched = response.factiiiList;
    }

    // Return items in the order the caller asked for. The previous
    // existing.concat(fetched) approach lost the input order, which broke
    // board-layout reordering — visual neighbors no longer matched
    // server-side pos ordering, so calculateNewPosition wrote a pos value
    // relative to the wrong neighbors.
    const byId = new Map<number, Factiii>();
    for (const factiii of pool) byId.set(factiii.id, factiii);
    for (const factiii of fetched) byId.set(factiii.id, factiii);

    const result: Factiii[] = [];
    for (const id of factiiiIds) {
      const factiii = byId.get(id);
      if (factiii) result.push(factiii);
    }
    return result;
  };

  const getFactiiiId = async (factiiiId: number) => {
    const factiii = factiiis.find(
      (factiii: Factiii) => factiiiId === factiii.id
    );
    return factiii ?? queryIds([factiiiId]);
  };

  const addFactiii = (newFactiii: Factiii) => mergeFactiiis([newFactiii]);

  const addFactiiis = (newFactiiis: Factiii[]) => mergeFactiiis(newFactiiis);

  const updateFactiiiState = (updated: Partial<Factiii> & { id: number }) =>
    queryClient.setQueryData(FACTIIIS_KEY, (current: Factiii[] | undefined) =>
      (current ?? EMPTY_FACTIIIS).map((factiii) =>
        factiii.id === updated.id ? { ...factiii, ...updated } : factiii
      )
    );

  return {
    factiiis,
    initializeFactiiis,
    getFactiiiIds,
    getFactiiiId,
    getFactiiiSlug,
    addFactiii,
    addFactiiis,
    updateFactiiiState
  };
};

export default useFactiiis;
